import { useState, useEffect } from "react";
import { apiClient } from "../api/client";
import type { EvaluationCriteria, Team } from "../api/types";
import Card from "../components/Card";
import { useAuth } from "../context/AuthContext";

interface TeamEvaluationForm {
  teamId: string;
  periodId: string;
  scores: Record<string, number>;
  comments: string;
}

function TeamEvaluationForm() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState<TeamEvaluationForm>({
    teamId: "",
    periodId: "",
    scores: {},
    comments: "",
  });

  // Fetch teams and criteria
  const fetchData = async () => {
    try {
      setLoading(true);
      const [teamsData, criteriaData] = await Promise.all([
        apiClient.get<{ teams: Team[] }>("/teams"),
        apiClient.get<{ criteria: EvaluationCriteria[] }>("/evaluation-criteria"),
      ]);

      setTeams(teamsData.teams || []);
      const criteriaList = criteriaData.criteria || [];
      setCriteria(criteriaList);

      // Initialize scores
      const scoresObj: Record<string, number> = {};
      criteriaList.forEach((c) => {
        scoresObj[c.id] = 3;
      });
      setFormData((prev) => ({
        ...prev,
        scores: scoresObj,
      }));

      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle score change
  const handleScoreChange = (criteriaId: string, score: number) => {
    setFormData((prev) => ({
      ...prev,
      scores: {
        ...prev.scores,
        [criteriaId]: score,
      },
    }));
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.teamId || !formData.periodId) {
      alert("Please select team and period");
      return;
    }

    try {
      setSubmitting(true);

      const scoresArray = criteria.map((c) => ({
        criteria_id: c.id,
        score: formData.scores[c.id] || 0,
      }));

      const payload = {
        teamId: formData.teamId,
        periodId: formData.periodId,
        scores: JSON.stringify(scoresArray),
        comments: formData.comments || null,
      };

      await apiClient.post("/team-evaluations", payload);
      alert("Team evaluation submitted successfully!");

      // Reset form
      setFormData({
        teamId: "",
        periodId: "",
        scores: {},
        comments: "",
      });
    } catch (err: any) {
      alert(err.message || "Failed to submit team evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate average score
  const averageScore =
    criteria.length > 0
      ? (Object.values(formData.scores).reduce((a, b) => a + b, 0) / criteria.length).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading team evaluation form...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Team Evaluation</h2>
        <p className="text-sm text-slate-500 mt-1">Evaluate overall team performance</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card title="Team Details">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Team *</label>
              <select
                value={formData.teamId}
                onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">-- Select Team --</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Evaluation Period *
              </label>
              <input
                type="text"
                value={formData.periodId}
                onChange={(e) => setFormData({ ...formData, periodId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter period ID"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Average Score</label>
              <div className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-900 font-semibold text-lg">
                {averageScore} / 5
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Team Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              placeholder="Provide feedback on team performance..."
            />
          </div>
        </Card>

        {/* Scoring Section */}
        <Card title={`Evaluation Criteria (${criteria.length} items)`}>
          <div className="space-y-6">
            {criteria.length === 0 ? (
              <p className="text-slate-500">
                No criteria defined. Please create evaluation criteria first.
              </p>
            ) : (
              criteria.map((c) => (
                <div key={c.id} className="border-b border-slate-200 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{c.name}</h4>
                      {c.description && (
                        <p className="text-sm text-slate-600 mt-1">{c.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                          {c.category}
                        </span>
                        <span className="text-xs text-slate-500">Weight: {c.weight}x</span>
                      </div>
                    </div>
                  </div>

                  {/* Score Slider */}
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max={c.maxScore}
                      step="1"
                      value={formData.scores[c.id] || 0}
                      onChange={(e) => handleScoreChange(c.id, parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-center min-w-16">
                      <div className="text-2xl font-bold text-indigo-600">
                        {formData.scores[c.id] || 0}
                      </div>
                      <div className="text-xs text-slate-500">/ {c.maxScore}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Submit Button */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting || criteria.length === 0 || !formData.teamId}
            className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? "Submitting..." : "Submit Team Evaluation"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TeamEvaluationForm;
